// frontend/src/utils/formatQuantity.ts
//
// Fixed-unit stock formatter: exactly 2 decimals, no unit rescaling. A nonzero
// value that would round to 0.00 shows a "<0.01" sentinel so a real tiny
// quantity is never displayed as zero; a null/undefined/non-finite value
// renders an em dash (never "undefined"/"NaN").
//
// Most stock displays now use formatMeasure.ts (which rescales to a readable
// unit, e.g. 18000 ml -> 18 L). formatQuantity is retained only where the
// displayed value must stay in the SAME unit as an adjacent entry field so the
// two don't disagree — currently the stock-count page's system-stock column,
// which sits beside a physical-count input whose variance math is in that unit.
//
// This does NOT render the Slice-4 unit-conflict state — a conflicted product
// (status 'conflict' / onHand null) is rendered by the caller as the
// <StatusPill> "needs review" badge. formatQuantity is only reached on the
// numeric (ok) path; its em-dash branch is a defensive backstop.

const EM_DASH = '—';

export function formatQuantity(
  value: number | null | undefined,
  unit?: string | null,
): string {
  if (value == null || !Number.isFinite(value)) return EM_DASH;
  const suffix = unit ? ` ${unit}` : '';
  // FP-safe 2dp rounding: (0.005).toFixed(2) is engine-dependent, so round the
  // scaled integer instead of trusting toFixed's binary-float rounding.
  // Add a tiny epsilon to handle FP precision issues (e.g., 1.005 * 100 = 100.49999...).
  // The epsilon is applied to the magnitude and the sign reapplied afterwards so
  // rounding is symmetric (round-half-away-from-zero) for negative values too —
  // otherwise adding a positive epsilon to a negative value pushes .xx5 ties
  // toward zero instead of away from it (e.g. -1.005 would wrongly round to -1.00).
  const rounded = Math.sign(value) * Math.round((Math.abs(value) + 1e-10) * 100) / 100;
  let num: string;
  if (rounded === 0) {
    // Either a genuine zero, or a nonzero value that rounds to 0.00 → sentinel.
    num = value === 0 ? '0.00' : value > 0 ? '<0.01' : '>-0.01';
  } else {
    num = rounded.toFixed(2);
  }
  return `${num}${suffix}`;
}
